import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  Injector,
  Type,
} from '@angular/core';
import '@diekeure/dcr-components/dcr-dialog.js';
import { DcrDialogRef } from './dcr-dialog-ref';

export interface DcrDialogConfig<D = unknown> {
  headline?: string;
  icon?: string;
  autoFocus?: boolean;
  type?: 'custom' | 'confirm' | 'confirm-cancel' | 'acknowledge' | 'alert';
  maxWidth?: string;
  fullscreen?: boolean;
  fitScreenHeight?: boolean;
  preventCloseByEscape?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  acknowledgeLabel?: string;
  /** Data passed to the dialog content component via DcrDialogRef.data. */
  data?: D;
}

export interface DcrDialogResult<D = unknown> {
  action: 'confirm' | 'close' | 'cancel';
  data?: D;
}

export interface DcrDialogCloser {
  _closeActive(action: DcrDialogResult['action']): void;
}

/**
 * Angular dialog service that dynamically creates dialog content components
 * inside a `<dcr-dialog>` element appended to `document.body`.
 *
 * No host component needed — works like Angular Material's MatDialog.
 *
 * @example
 * ```typescript
 * const result = await this.dialogService.open(NameFormComponent, {
 *   headline: 'Geef een naam',
 *   data: { prefill: 'Karl' },
 * });
 *
 * if (result.action === 'confirm') {
 *   console.log(result.data); // { name: '...' }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class DcrDialogService implements DcrDialogCloser {
  private _appRef = inject(ApplicationRef);
  private _injector = inject(Injector);
  private _envInjector = inject(EnvironmentInjector);

  private _dialogEl: HTMLElement | null = null;
  private _componentRef: ComponentRef<unknown> | null = null;
  private _dialogRef: DcrDialogRef | null = null;
  private _resolve: ((result: DcrDialogResult<any>) => void) | null = null;

  private readonly _onClosedHandler = () => this._onClosed();
  private readonly _onConfirmHandler = () => this._closeActive('confirm');
  private readonly _onCancelHandler = () => this._closeActive('cancel');

  open<D = unknown>(component: Type<unknown>, config: DcrDialogConfig<D> = {}): Promise<DcrDialogResult<D>> {
    if (this._resolve) {
      this._resolve({ action: 'close' });
      this._resolve = null;
    }
    this._cleanup();

    const dialogRef = new DcrDialogRef<D>();
    dialogRef.data = config.data;
    dialogRef._service = this;
    this._dialogRef = dialogRef;

    // Create <dcr-dialog> element
    const dialogEl = document.createElement('dcr-dialog');
    this._applyConfig(dialogEl, config);
    document.body.appendChild(dialogEl);
    this._dialogEl = dialogEl;

    // Create the Angular component inside the dialog
    const childInjector = Injector.create({
      providers: [{ provide: DcrDialogRef, useValue: dialogRef }],
      parent: this._injector,
    });

    const componentRef = createComponent(component, {
      environmentInjector: this._envInjector,
      elementInjector: childInjector,
    });
    this._componentRef = componentRef;

    // Append the component's DOM into the dialog
    dialogEl.appendChild(componentRef.location.nativeElement);
    this._appRef.attachView(componentRef.hostView);

    // Listen for dialog events
    dialogEl.addEventListener('closed', this._onClosedHandler);
    dialogEl.addEventListener('confirm', this._onConfirmHandler);
    dialogEl.addEventListener('cancel', this._onCancelHandler);

    // Open the dialog
    dialogEl.setAttribute('open', '');

    return new Promise<DcrDialogResult<D>>((resolve) => {
      this._resolve = resolve;
    });
  }

  /** @internal Called by DcrDialogRef or event listeners to resolve the promise and close. */
  _closeActive(action: DcrDialogResult['action'] = 'close', data?: unknown): void {
    const resolvedData = data ?? this._dialogRef?.data;

    if (this._resolve) {
      this._resolve({ action, data: resolvedData });
      this._resolve = null;
    }

    // Setting open to false triggers the close animation, then the 'closed' event fires
    if (this._dialogEl) {
      this._dialogEl.removeAttribute('open');
    }
  }

  private _onClosed(): void {
    // If the dialog was closed via escape or scrim click, resolve the promise
    if (this._resolve) {
      this._resolve({ action: 'close' });
      this._resolve = null;
    }
    this._cleanup();
  }

  private _applyConfig(el: HTMLElement, config: DcrDialogConfig): void {
    if (config.headline) el.setAttribute('headline', config.headline);
    if (config.icon) el.setAttribute('icon', config.icon);
    if (config.type) el.setAttribute('type', config.type);
    if (config.autoFocus) el.setAttribute('auto-focus', '');
    if (config.fullscreen) el.setAttribute('fullscreen', '');
    if (config.fitScreenHeight) el.setAttribute('fit-screen-height', '');
    if (config.preventCloseByEscape) el.setAttribute('prevent-close-by-escape', '');
    if (config.confirmLabel) el.setAttribute('confirm-label', config.confirmLabel);
    if (config.cancelLabel) el.setAttribute('cancel-label', config.cancelLabel);
    if (config.acknowledgeLabel) el.setAttribute('acknowledge-label', config.acknowledgeLabel);
    if (config.maxWidth) el.style.setProperty('--dcr-comp-dialog-max-width', config.maxWidth);
  }

  private _cleanup(): void {
    if (this._dialogEl) {
      this._dialogEl.removeEventListener('closed', this._onClosedHandler);
      this._dialogEl.removeEventListener('confirm', this._onConfirmHandler);
      this._dialogEl.removeEventListener('cancel', this._onCancelHandler);
      this._dialogEl.remove();
      this._dialogEl = null;
    }

    if (this._componentRef) {
      this._appRef.detachView(this._componentRef.hostView);
      this._componentRef.destroy();
      this._componentRef = null;
    }

    this._dialogRef = null;
  }
}
